/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useEffect } from 'react';
import type { Product } from '@shared/types/products';
import { useToast } from '@/hooks/use-toast';
import { useChatContext } from '@shared/contexts/ChatContext';
import { useApp } from '../../contexts/AppContext';
import { SkillShopService } from '../../services/skillShopService';
import { ProductFocusTabMenu, ProductFocusTab } from './ProductFocusTabMenu';
import { getDefaultRepoRelativePath } from '../../utils/repoPaths';
import { pushLocalRepoToRemote, createRemoteRepository, cloneRepository, checkLocalChanges, resetLocalRepository } from '../../lib/api/git-provider';
import { listTemplates, initializeProject, checkRepoInitialized, applyTemplate } from '../../lib/api/templates';
import { GitHubService, type GitHubRepository, type GitHubOrganization } from '../../services/githubService';
import type { CheckRepoInitializedResult, LocalChangesStatus } from '../../../server/types';
import { GitLabService, type GitLabProject } from '../../services/gitlabService';
import type { TemplateMetadata } from '../../../server/types';
import {
  OverviewTab,
  SourceControlTab,
  DocumentationTab,
  ArchitectureTab,
  TestingTab,
  type RepoConfigMode,
  type TestingPlanScope,
  type TestingPlanResult,
} from './tabs';
import type { Issue } from '@shared/types/issues';
import {
  generateTestingPlan,
  getReleases,
  generatePlaywrightTests,
  runPlaywrightTests,
  getTestResults,
  getGeneratedFeatureAreas,
  createBugIssues,
  type GeneratedFeatureArea,
  type TestFailure,
  type TestRunSummary,
} from '../../services/skillshop';

interface ProductFocusViewProps {
  product: Product;
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
  onProductUpdated: () => void;
}

export function ProductFocusView({ product, onEdit, onDelete, onProductUpdated }: ProductFocusViewProps) {
  const [activeTab, setActiveTab] = useState<ProductFocusTab>('overview');
  const [isRunningDocAgent, setIsRunningDocAgent] = useState(false);
  const [docAgentOutput, setDocAgentOutput] = useState<string | null>(null);
  const { toast } = useToast();
  const { executeAgentWithStreaming, state: chatState } = useChatContext();
  const { setActiveProduct, activeProduct, sourceControlProvider, githubConfig, gitlabConfig } = useApp();
  const [skillShopService] = useState(() => new SkillShopService());
  
  // Push to remote state
  const [isPushingToRemote, setIsPushingToRemote] = useState(false);
  const [newRepoName, setNewRepoName] = useState('');
  const [newRepoDescription, setNewRepoDescription] = useState('');
  const [newRepoIsPrivate, setNewRepoIsPrivate] = useState(true);
  const [showCreateRepoForm, setShowCreateRepoForm] = useState(false);
  
  // Organization selection state (for pushing to remote)
  const [organizations, setOrganizations] = useState<GitHubOrganization[]>([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<string>(''); // Empty string = personal account

  // Repository configuration state (for products without repos)
  const [repoConfigMode, setRepoConfigMode] = useState<RepoConfigMode>('none');
  const [templates, setTemplates] = useState<TemplateMetadata[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [isInitializingRepo, setIsInitializingRepo] = useState(false);
  const [availableRepos, setAvailableRepos] = useState<(GitHubRepository | GitLabProject)[]>([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [selectedRepoUrl, setSelectedRepoUrl] = useState<string>('');
  const [isLinkingRepo, setIsLinkingRepo] = useState(false);

  // Indexing state - controlled by the handleIndexCodebase function
  const [isIndexing, setIsIndexing] = useState(false);

  // Repository initialization status (for detecting blank repos that need templates)
  const [repoInitStatus, setRepoInitStatus] = useState<CheckRepoInitializedResult | null>(null);
  const [isCheckingRepoInit, setIsCheckingRepoInit] = useState(false);
  const [isApplyingTemplate, setIsApplyingTemplate] = useState(false);
  
  // Repository reset state
  const [isCheckingLocalChanges, setIsCheckingLocalChanges] = useState(false);
  const [localChangesStatus, setLocalChangesStatus] = useState<LocalChangesStatus | null>(null);
  const [isResettingRepo, setIsResettingRepo] = useState(false);

  // Testing plan state
  const [releases, setReleases] = useState<Issue[]>([]);
  const [isGeneratingTestingPlan, setIsGeneratingTestingPlan] = useState(false);
  const [lastGeneratedPlan, setLastGeneratedPlan] = useState<TestingPlanResult | null>(null);

  // Playwright test automation state
  const [isGeneratingTests, setIsGeneratingTests] = useState(false);
  const [isRunningTests, setIsRunningTests] = useState(false);
  const [isCreatingBugs, setIsCreatingBugs] = useState(false);
  const [generatedFeatureAreas, setGeneratedFeatureAreas] = useState<GeneratedFeatureArea[]>([]);
  const [testResults, setTestResults] = useState<{
    summary?: TestRunSummary;
    failures?: TestFailure[];
    reportPath?: string;
  } | null>(null);

  // Derive local path for the repository
  const localPath = product.repositoryUrl 
    ? (() => {
        const basePath = getDefaultRepoRelativePath(product.repositoryUrl);
        const repoPathSegment = product.repoPath
          ? product.repoPath.replace(/^[\\/]+/, '').replace(/[\\/]+$/, '')
          : '';
        return repoPathSegment ? `${basePath}/${repoPathSegment}` : basePath;
      })()
    : null;

  // Get the absolute path for the repository worktree (for code indexing)
  // The format is ~/.velocity/repos/<owner_repo>/velocity[/<repo-path>]
  const getAbsoluteRepoPath = (): string | null => {
    if (!product.repositoryUrl) return null;
    
    // Extract owner_repo from the URL
    const repoUrl = product.repositoryUrl;
    let repoIdentifier = '';
    
    // Try to extract owner/repo from GitHub URL
    const githubMatch = repoUrl.match(/github\.com[:/]+([^/]+)\/([^/]+?)(?:\.git)?$/i);
    if (githubMatch) {
      repoIdentifier = `${githubMatch[1]}_${githubMatch[2]}`.toLowerCase();
    } else {
      // Fallback: use the URL as identifier (simplified)
      repoIdentifier = repoUrl.replace(/[^a-zA-Z0-9_-]/g, '_');
    }
    
    // Build the path: ~/.velocity/repos/<repo-identifier>/velocity[/<repo-path>]
    // Note: We need to expand ~ on the server side, so we use a template
    const homePath = '~/.velocity/repos';
    const velocityWorktree = `${homePath}/${repoIdentifier}/velocity`;
    
    if (product.repoPath) {
      const sanitizedRepoPath = product.repoPath.replace(/^[\\/]+/, '').replace(/[\\/]+$/, '');
      return sanitizedRepoPath ? `${velocityWorktree}/${sanitizedRepoPath}` : velocityWorktree;
    }
    
    return velocityWorktree;
  };

  const absoluteRepoPath = getAbsoluteRepoPath();

  // Load templates and repositories when Source Control tab is active and no repo is configured
  useEffect(() => {
    if (activeTab === 'source-control' && !product.repositoryUrl) {
      loadTemplates();
      loadAvailableRepositories();
    }
  }, [activeTab, product.repositoryUrl, sourceControlProvider, githubConfig?.accessToken, gitlabConfig?.accessToken]);

  // Check repo initialization status when Source Control or Architecture tab is active and repo exists
  useEffect(() => {
    if ((activeTab === 'source-control' || activeTab === 'architecture') && product.repositoryUrl && absoluteRepoPath) {
      checkRepoInitializationStatus();
      // Also load templates in case the repo is uninitialized
      loadTemplates();
    }
  }, [activeTab, product.repositoryUrl, absoluteRepoPath, product.id]);
  
  // Load organizations when push form is shown (for GitHub)
  useEffect(() => {
    if (showCreateRepoForm && sourceControlProvider === 'github' && githubConfig?.accessToken) {
      loadOrganizations();
    }
  }, [showCreateRepoForm, sourceControlProvider, githubConfig?.accessToken]);

  // Check if the repository has been initialized with a template
  const checkRepoInitializationStatus = async () => {
    if (!absoluteRepoPath) return;

    setIsCheckingRepoInit(true);
    try {
      const result = await checkRepoInitialized(absoluteRepoPath);
      setRepoInitStatus(result);
    } catch (error) {
      console.error('Error checking repo initialization:', error);
      setRepoInitStatus(null);
    } finally {
      setIsCheckingRepoInit(false);
    }
  };

  // Apply a template to an existing blank repository
  const handleApplyTemplateToExistingRepo = async () => {
    if (!selectedTemplate || !absoluteRepoPath) {
      toast({
        title: 'Select a template',
        description: 'Please select a template to apply.',
        variant: 'destructive',
      });
      return;
    }

    setIsApplyingTemplate(true);
    try {
      const result = await applyTemplate({
        repoPath: absoluteRepoPath,
        template: selectedTemplate,
        projectName: product.title,
        options: {},
      });

      if (result.success) {
        toast({
          title: 'Template applied',
          description: `Repository initialized with ${selectedTemplate} template. ${result.filesWritten} files written.`,
        });
        
        // Refresh the repo initialization status
        await checkRepoInitializationStatus();
        setSelectedTemplate('');
        
        // Notify parent to refresh product data
        onProductUpdated();
      } else {
        toast({
          title: 'Failed to apply template',
          description: result.error || 'An error occurred while applying the template.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Error applying template:', error);
      toast({
        title: 'Error',
        description: error.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    } finally {
      setIsApplyingTemplate(false);
    }
  };

  // Load available templates
  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const result = await listTemplates();
      if (result.success && result.templates) {
        setTemplates(result.templates);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Load available repositories from GitHub/GitLab
  const loadAvailableRepositories = async () => {
    const hasConfig = sourceControlProvider === 'gitlab' 
      ? !!gitlabConfig?.accessToken 
      : !!githubConfig?.accessToken;
    
    if (!hasConfig) {
      setAvailableRepos([]);
      return;
    }

    setLoadingRepos(true);
    try {
      if (sourceControlProvider === 'github' && githubConfig?.accessToken) {
        const service = new GitHubService(githubConfig);
        const repos = await service.getRepositories();
        setAvailableRepos(repos);
      } else if (sourceControlProvider === 'gitlab' && gitlabConfig?.accessToken) {
        const service = new GitLabService(gitlabConfig);
        const projects = await service.getProjects();
        setAvailableRepos(projects);
      }
    } catch (error) {
      console.error('Failed to load repositories:', error);
      setAvailableRepos([]);
    } finally {
      setLoadingRepos(false);
    }
  };
  
  // Load GitHub organizations for the push to remote feature
  const loadOrganizations = async () => {
    if (sourceControlProvider !== 'github' || !githubConfig?.accessToken) {
      setOrganizations([]);
      return;
    }
    
    setLoadingOrganizations(true);
    try {
      const service = new GitHubService(githubConfig);
      const orgs = await service.getOrganizations();
      setOrganizations(orgs);
    } catch (error) {
      console.error('Failed to load organizations:', error);
      setOrganizations([]);
    } finally {
      setLoadingOrganizations(false);
    }
  };

  // Handle initializing repository from template
  const handleInitializeFromTemplate = async () => {
    if (!selectedTemplate) {
      toast({
        title: 'Select a template',
        description: 'Please select a template to initialize the repository.',
        variant: 'destructive',
      });
      return;
    }

    setIsInitializingRepo(true);
    try {
      const result = await initializeProject({
        name: product.title,
        template: selectedTemplate,
        path: '', // Will be determined by the service based on worktree format
        options: {},
        useWorktree: true, // Use the new worktree format
      });

      if (result.success && result.repository) {
        // Update the product with the new local repository URL
        // Format: local/<repo-identifier>
        const localRepoUrl = result.repository;
        
        try {
          await skillShopService.updateProduct(product.id, {
            repositoryUrl: localRepoUrl,
          });
          
          toast({
            title: 'Repository initialized',
            description: `Repository created from ${selectedTemplate} template and linked to product.`,
          });
          
          // Refresh the product list to show the updated repository
          onProductUpdated();
          setRepoConfigMode('none');
          setSelectedTemplate('');
        } catch (updateError: any) {
          console.error('Error updating product with repository URL:', updateError);
          toast({
            title: 'Partial success',
            description: `Repository created but failed to update product: ${updateError.message}`,
            variant: 'destructive',
          });
        }
      } else {
        toast({
          title: 'Initialization failed',
          description: result.error || 'Failed to initialize repository.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Error initializing repository:', error);
      toast({
        title: 'Error',
        description: error.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    } finally {
      setIsInitializingRepo(false);
    }
  };

  // Handle linking to an existing remote repository
  const handleLinkToRemote = async () => {
    if (!selectedRepoUrl) {
      toast({
        title: 'Select a repository',
        description: 'Please select a repository to link.',
        variant: 'destructive',
      });
      return;
    }

    const accessToken = sourceControlProvider === 'gitlab' 
      ? gitlabConfig?.accessToken 
      : githubConfig?.accessToken;

    if (!accessToken) {
      toast({
        title: 'Authentication required',
        description: `Please configure your ${sourceControlProvider === 'gitlab' ? 'GitLab' : 'GitHub'} access token in Settings.`,
        variant: 'destructive',
      });
      return;
    }

    setIsLinkingRepo(true);
    try {
      // Clone the repository
      const result = await cloneRepository(
        selectedRepoUrl,
        product.title,
        product.repoPath,
        accessToken,
        sourceControlProvider
      );

      if (result.success) {
        toast({
          title: 'Repository linked',
          description: 'The repository has been cloned and linked to this product.',
        });
        // Update the product with the new repository URL
        onProductUpdated();
        setRepoConfigMode('none');
        setSelectedRepoUrl('');
      } else {
        toast({
          title: 'Linking failed',
          description: result.error || 'Failed to link repository.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Error linking repository:', error);
      toast({
        title: 'Error',
        description: error.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    } finally {
      setIsLinkingRepo(false);
    }
  };

  // Handle code indexing - uses the appropriate backend
  // Note: DocumentationTab manages its own status refresh after indexing completes
  const handleIndexCodebase = async (forceReindex: boolean = false): Promise<void> => {
    if (!absoluteRepoPath || !product.title) return;

    setIsIndexing(true);

    try {
      // Try vector search first (preferred for Electron app)
      const serverStatus = await skillShopService.getVectorSearchStatus();
      
      if (serverStatus.connected) {
        // Use vector search - server will expand ~ in path
        const result = await skillShopService.ensureVectorSearchRepoIndexed(
          absoluteRepoPath,
          product.title,
          forceReindex
        );

        if (result.success) {
          if (result.indexingStarted) {
            toast({
              title: 'Indexing started',
              description: 'Indexing is running in the background. Status will update as it completes.',
            });
          } else {
            toast({
              title: 'Codebase indexed',
              description: 'Your codebase is ready for semantic search.',
            });
          }
        } else if (result.error?.toLowerCase().includes('already indexed')) {
          // "Already indexed" is a success condition, not an error
          toast({
            title: 'Codebase ready',
            description: 'Your codebase is already indexed for semantic search.',
          });
        } else {
          toast({
            title: 'Indexing failed',
            description: result.error || 'Unable to index codebase. Please try again.',
            variant: 'destructive',
          });
        }
      } else {
        // Fall back to claude-context - server will expand ~ in path
        const result = await skillShopService.indexCodebase(absoluteRepoPath, forceReindex);
        
        if (result.success) {
          const indexingStarted = result.message?.toLowerCase().includes('background') ?? false;
          if (indexingStarted) {
            toast({
              title: 'Indexing started',
              description: 'Indexing is running in the background. Status will update as it completes.',
            });
          } else {
            toast({
              title: 'Codebase indexed',
              description: 'Your codebase is ready for semantic search.',
            });
          }
        } else if (result.error?.toLowerCase().includes('already indexed')) {
          // "Already indexed" is a success condition, not an error
          toast({
            title: 'Codebase ready',
            description: 'Your codebase is already indexed for semantic search.',
          });
        } else {
          toast({
            title: 'Indexing failed',
            description: result.error || 'Unable to index codebase. Please try again.',
            variant: 'destructive',
          });
        }
      }
    } catch (error: any) {
      console.error('Error indexing codebase:', error);
      toast({
        title: 'Indexing error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsIndexing(false);
    }
  };

  const handleClearIndex = async () => {
    if (!absoluteRepoPath) return;

    try {
      // Server will expand ~ in path
      const result = await skillShopService.clearCodeIndex(absoluteRepoPath);
      
      if (result.success) {
        toast({
          title: 'Index cleared',
          description: 'The code index has been cleared.',
        });
      } else {
        toast({
          title: 'Failed to clear index',
          description: result.error || 'An error occurred.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Error clearing index:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to clear code index.',
        variant: 'destructive',
      });
    }
  };

  const handleRunDocumentationAgent = async () => {
    if (!product) return;

    setIsRunningDocAgent(true);
    setDocAgentOutput(null);

    try {
      // Ensure this product is set as active so the agent has context
      if (!activeProduct || activeProduct.id !== product.id) {
        setActiveProduct(product);
      }

      // Execute the documentation_initializer agent
      const message = `Initialize documentation for product "${product.title}" (${product.productId}). Please analyze the codebase and generate comprehensive documentation following Velocity's documentation standards.`;
      
      const result = await executeAgentWithStreaming('documentation_initializer', message);
      
      setDocAgentOutput(result);
      toast({
        title: 'Documentation agent completed',
        description: 'Documentation has been generated for this product.',
      });
    } catch (error: any) {
      console.error('Error running documentation agent:', error);
      toast({
        title: 'Documentation agent failed',
        description: error.message || 'Failed to run documentation agent.',
        variant: 'destructive',
      });
      setDocAgentOutput(`Error: ${error.message}`);
    } finally {
      setIsRunningDocAgent(false);
    }
  };

  const handleSetAsActive = () => {
    setActiveProduct(product);
    toast({
      title: 'Active product changed',
      description: `${product.title} is now the active product.`,
    });
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${product.title}"?`)) {
      onDelete(product);
    }
  };

  // Check for local changes before resetting the repository
  const handleCheckLocalChanges = async () => {
    if (!product.repositoryUrl) return;

    setIsCheckingLocalChanges(true);
    try {
      const result = await checkLocalChanges(product.repositoryUrl, sourceControlProvider);
      
      if (result.success && result.status) {
        setLocalChangesStatus(result.status);
      } else {
        // If check fails, assume no changes to allow reset
        setLocalChangesStatus({
          hasChanges: false,
          hasUncommittedChanges: false,
          uncommittedFiles: [],
          aheadOfMain: 0,
          unpushedCommits: 0
        });
        if (result.error) {
          console.warn('Error checking local changes:', result.error);
        }
      }
    } catch (error: any) {
      console.error('Error checking local changes:', error);
      // Assume no changes on error to allow reset
      setLocalChangesStatus({
        hasChanges: false,
        hasUncommittedChanges: false,
        uncommittedFiles: [],
        aheadOfMain: 0,
        unpushedCommits: 0
      });
    } finally {
      setIsCheckingLocalChanges(false);
    }
  };

  // Reset the local repository by wiping and re-cloning from remote
  const handleResetRepo = async (force: boolean) => {
    if (!product.repositoryUrl) return;

    const accessToken = sourceControlProvider === 'gitlab' 
      ? gitlabConfig?.accessToken 
      : githubConfig?.accessToken;

    setIsResettingRepo(true);
    try {
      const result = await resetLocalRepository(
        product.repositoryUrl,
        accessToken,
        sourceControlProvider,
        sourceControlProvider === 'gitlab' ? gitlabConfig?.instanceUrl : undefined,
        force
      );

      if (result.success) {
        toast({
          title: 'Repository reset',
          description: 'Local repository has been reset and re-cloned from remote.',
        });
        
        // Clear the local changes status
        setLocalChangesStatus(null);
        
        // Refresh the repo initialization status
        await checkRepoInitializationStatus();
        
        // Notify parent to refresh product data
        onProductUpdated();
      } else if (result.action === 'skipped') {
        // This shouldn't happen since we're using force, but handle it anyway
        toast({
          title: 'Reset skipped',
          description: result.message || 'Local changes detected. Please try again with confirmation.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Reset failed',
          description: result.error || 'Failed to reset the repository.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Error resetting repository:', error);
      toast({
        title: 'Error',
        description: error.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    } finally {
      setIsResettingRepo(false);
    }
  };

  // Fetch releases for testing plan generation
  const handleRefreshReleases = async () => {
    try {
      const result = await getReleases(product.repositoryUrl ?? undefined, product.repoPath ?? undefined);
      if (result.success) {
        setReleases(result.releases);
      } else {
        console.error('Failed to fetch releases:', result.error);
        setReleases([]);
      }
    } catch (error) {
      console.error('Error fetching releases:', error);
      setReleases([]);
    }
  };

  // Generate a testing plan for the product or a release
  const handleGenerateTestingPlan = async (scope: TestingPlanScope, releaseId?: string) => {
    setIsGeneratingTestingPlan(true);
    setLastGeneratedPlan(null);

    try {
      const result = await generateTestingPlan(
        scope,
        {
          id: product.id,
          title: product.title,
          description: product.description,
          repositoryUrl: product.repositoryUrl ?? undefined,
          repoPath: product.repoPath ?? undefined,
        },
        releaseId
      );

      setLastGeneratedPlan(result);

      if (result.success) {
        toast({
          title: 'Testing plan generated',
          description: `Testing plan saved to ${result.fileName}`,
        });
      } else {
        toast({
          title: 'Failed to generate testing plan',
          description: result.error || 'An error occurred while generating the testing plan.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Error generating testing plan:', error);
      const errorResult: TestingPlanResult = {
        success: false,
        error: error.message || 'An unexpected error occurred.',
      };
      setLastGeneratedPlan(errorResult);
      toast({
        title: 'Error',
        description: error.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    } finally {
      setIsGeneratingTestingPlan(false);
    }
  };

  // Refresh generated feature areas
  const handleRefreshFeatureAreas = async () => {
    try {
      const result = await getGeneratedFeatureAreas();
      if (result.success) {
        setGeneratedFeatureAreas(result.featureAreas);
      }
    } catch (error: any) {
      console.error('Error fetching feature areas:', error);
    }
  };

  // Refresh test results
  const handleRefreshTestResults = async () => {
    try {
      const result = await getTestResults();
      if (result.success) {
        setTestResults({
          summary: result.summary,
          failures: result.failures,
          reportPath: result.reportPath,
        });
      }
    } catch (error: any) {
      console.error('Error fetching test results:', error);
    }
  };

  // Generate Playwright tests from the testing plan
  const handleGeneratePlaywrightTests = async () => {
    setIsGeneratingTests(true);

    try {
      const result = await generatePlaywrightTests(
        product.repositoryUrl ?? undefined,
        product.repoPath ?? undefined,
        'http://localhost:3001'
      );

      if (result.success) {
        setGeneratedFeatureAreas(result.featureAreas || []);
        toast({
          title: 'Tests generated',
          description: `Generated tests for ${result.featureAreas?.length || 0} feature areas.`,
        });
      } else {
        toast({
          title: 'Failed to generate tests',
          description: result.error || 'An error occurred while generating tests.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Error generating tests:', error);
      toast({
        title: 'Error',
        description: error.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    } finally {
      setIsGeneratingTests(false);
    }
  };

  // Run Playwright tests
  const handleRunTests = async (featureArea?: string) => {
    setIsRunningTests(true);
    setTestResults(null);

    try {
      const result = await runPlaywrightTests(featureArea);

      if (result.success || result.testResults) {
        await handleRefreshTestResults();
        toast({
          title: result.success ? 'Tests passed' : 'Tests completed with failures',
          description: result.success 
            ? 'All tests passed successfully.' 
            : 'Some tests failed. Review the results below.',
          variant: result.success ? 'default' : 'destructive',
        });
      } else {
        toast({
          title: 'Failed to run tests',
          description: result.error || 'An error occurred while running tests.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Error running tests:', error);
      toast({
        title: 'Error',
        description: error.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    } finally {
      setIsRunningTests(false);
    }
  };

  // Create bug issues from test failures
  const handleCreateBugs = async (failures: TestFailure[]) => {
    setIsCreatingBugs(true);

    try {
      const result = await createBugIssues(
        failures,
        product.repositoryUrl ?? undefined,
        product.repoPath ?? undefined
      );

      if (result.success) {
        toast({
          title: 'Bugs created',
          description: `Created ${result.createdIssues?.length || 0} bug issues.`,
        });
      } else {
        toast({
          title: 'Failed to create bugs',
          description: result.error || 'An error occurred while creating bugs.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Error creating bugs:', error);
      toast({
        title: 'Error',
        description: error.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingBugs(false);
    }
  };

  // Derived values for tab components
  const isActive = activeProduct?.id === product.id;
  const isLocalOnlyRepo = product.repositoryUrl?.startsWith('local/');
  const hasProviderConfig = sourceControlProvider === 'gitlab' 
    ? !!gitlabConfig?.accessToken 
    : !!githubConfig?.accessToken;

  // Render tab content using extracted components
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <OverviewTab
            product={product}
            isActive={isActive}
            onEdit={onEdit}
            onDelete={handleDelete}
            onSetAsActive={handleSetAsActive}
          />
        );
      case 'source-control':
        return (
          <SourceControlTab
            product={product}
            localPath={localPath}
            sourceControlProvider={sourceControlProvider}
            hasProviderConfig={hasProviderConfig}
            repoInitStatus={repoInitStatus}
            templates={templates}
            loadingTemplates={loadingTemplates}
            selectedTemplate={selectedTemplate}
            onTemplateSelect={setSelectedTemplate}
            isApplyingTemplate={isApplyingTemplate}
            onApplyTemplate={handleApplyTemplateToExistingRepo}
            showCreateRepoForm={showCreateRepoForm}
            onShowCreateRepoForm={setShowCreateRepoForm}
            newRepoName={newRepoName}
            onNewRepoNameChange={setNewRepoName}
            newRepoDescription={newRepoDescription}
            onNewRepoDescriptionChange={setNewRepoDescription}
            newRepoIsPrivate={newRepoIsPrivate}
            onNewRepoIsPrivateChange={setNewRepoIsPrivate}
            isPushingToRemote={isPushingToRemote}
            onPushToRemote={handlePushToRemote}
            repoConfigMode={repoConfigMode}
            onRepoConfigModeChange={setRepoConfigMode}
            availableRepos={availableRepos}
            loadingRepos={loadingRepos}
            selectedRepoUrl={selectedRepoUrl}
            onSelectedRepoUrlChange={setSelectedRepoUrl}
            isLinkingRepo={isLinkingRepo}
            onLinkToRemote={handleLinkToRemote}
            isInitializingRepo={isInitializingRepo}
            onInitializeFromTemplate={handleInitializeFromTemplate}
            isCheckingLocalChanges={isCheckingLocalChanges}
            localChangesStatus={localChangesStatus}
            isResettingRepo={isResettingRepo}
            onCheckLocalChanges={handleCheckLocalChanges}
            onResetRepo={handleResetRepo}
            organizations={organizations}
            loadingOrganizations={loadingOrganizations}
            selectedOrganization={selectedOrganization}
            onSelectedOrganizationChange={setSelectedOrganization}
          />
        );
      case 'documentation':
        return (
          <DocumentationTab
            product={product}
            isIndexing={isIndexing}
            isRunningDocAgent={isRunningDocAgent}
            isExecuting={chatState.isExecuting}
            docAgentOutput={docAgentOutput}
            onIndexCodebase={handleIndexCodebase}
            onRunDocAgent={handleRunDocumentationAgent}
          />
        );
      case 'architecture':
        return (
          <ArchitectureTab
            product={product}
            repoInitStatus={repoInitStatus}
            templates={templates}
            loadingTemplates={loadingTemplates}
            selectedTemplate={selectedTemplate}
            onTemplateSelect={setSelectedTemplate}
            isApplyingTemplate={isApplyingTemplate}
            onApplyTemplate={handleApplyTemplateToExistingRepo}
            onEdit={onEdit}
          />
        );
      case 'testing':
        return (
          <TestingTab
            product={product}
            releases={releases}
            isGenerating={isGeneratingTestingPlan}
            lastGeneratedPlan={lastGeneratedPlan}
            onGenerateTestingPlan={handleGenerateTestingPlan}
            onRefreshReleases={handleRefreshReleases}
            onGeneratePlaywrightTests={handleGeneratePlaywrightTests}
            onRunTests={handleRunTests}
            onCreateBugs={handleCreateBugs}
            isGeneratingTests={isGeneratingTests}
            isRunningTests={isRunningTests}
            isCreatingBugs={isCreatingBugs}
            generatedFeatureAreas={generatedFeatureAreas}
            testResults={testResults}
            onRefreshFeatureAreas={handleRefreshFeatureAreas}
            onRefreshTestResults={handleRefreshTestResults}
          />
        );
      default:
        return (
          <OverviewTab
            product={product}
            isActive={isActive}
            onEdit={onEdit}
            onDelete={handleDelete}
            onSetAsActive={handleSetAsActive}
          />
        );
    }
  };

  // Get the local container path for the repository
  const getLocalContainerPath = (): string | null => {
    if (!product.repositoryUrl) return null;
    
    if (isLocalOnlyRepo) {
      // Local repo: local/my-project -> local_my-project
      const repoIdentifier = product.repositoryUrl.replace('local/', '');
      return `~/.velocity/repos/local_${repoIdentifier}`;
    }
    
    // Remote repo: extract owner_repo from URL
    const githubMatch = product.repositoryUrl.match(/github\.com[:/]+([^/]+)\/([^/]+?)(?:\.git)?$/i);
    if (githubMatch) {
      return `~/.velocity/repos/${githubMatch[1]}_${githubMatch[2]}`.toLowerCase();
    }
    
    return null;
  };
  
  // Handle pushing local repo to remote
  const handlePushToRemote = async () => {
    if (!product.repositoryUrl || !isLocalOnlyRepo) return;
    
    const accessToken = sourceControlProvider === 'gitlab' 
      ? gitlabConfig?.accessToken 
      : githubConfig?.accessToken;
    
    if (!accessToken) {
      toast({
        title: 'Authentication required',
        description: `Please configure your ${sourceControlProvider === 'gitlab' ? 'GitLab' : 'GitHub'} access token in Settings.`,
        variant: 'destructive',
      });
      return;
    }
    
    if (!newRepoName.trim()) {
      toast({
        title: 'Repository name required',
        description: 'Please enter a name for the remote repository.',
        variant: 'destructive',
      });
      return;
    }
    
    setIsPushingToRemote(true);
    
    try {
      // Step 1: Create the remote repository
      const createResult = await createRemoteRepository(
        newRepoName.trim(),
        newRepoDescription.trim() || undefined,
        newRepoIsPrivate,
        accessToken,
        sourceControlProvider === 'gitlab' ? gitlabConfig?.instanceUrl : undefined,
        sourceControlProvider,
        selectedOrganization || undefined // Pass selected org (undefined for personal account)
      );
      
      if (!createResult.success || !createResult.repository) {
        toast({
          title: 'Failed to create repository',
          description: createResult.error || 'Could not create the remote repository.',
          variant: 'destructive',
        });
        return;
      }
      
      // Step 2: Push local repo to the new remote
      const containerPath = getLocalContainerPath();
      if (!containerPath) {
        toast({
          title: 'Repository path not found',
          description: 'Could not determine the local repository path.',
          variant: 'destructive',
        });
        return;
      }
      
      // Send path with ~ - server will expand it to the actual home directory
      const pushResult = await pushLocalRepoToRemote(
        containerPath,
        createResult.repository.cloneUrl,
        accessToken,
        sourceControlProvider === 'gitlab' ? gitlabConfig?.instanceUrl : undefined,
        sourceControlProvider
      );
      
      if (!pushResult.success) {
        toast({
          title: 'Failed to push to remote',
          description: pushResult.error || 'Could not push the local repository to the remote.',
          variant: 'destructive',
        });
        return;
      }
      
      toast({
        title: 'Repository pushed successfully',
        description: `Your repository is now available at ${createResult.repository.htmlUrl}`,
      });
      
      // Reset form
      setShowCreateRepoForm(false);
      setNewRepoName('');
      setNewRepoDescription('');
      setNewRepoIsPrivate(true);
      setSelectedOrganization('');
      
      // Trigger product update to refresh the repository URL
      onProductUpdated();
      
    } catch (error: any) {
      console.error('Error pushing to remote:', error);
      toast({
        title: 'Error',
        description: error.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    } finally {
      setIsPushingToRemote(false);
    }
  };

  return (
    <div className="flex gap-6 h-full">
      {/* Tab Menu */}
      <div className="shrink-0 py-2">
        <ProductFocusTabMenu
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        {renderTabContent()}
      </div>
    </div>
  );
}

